import type { FormEvent, ReactNode } from 'react';
import { Button } from './button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { cn } from './utils';

/* -------------------------------------------------------------------------- */
/*  AppModal                                                                  */
/*                                                                            */
/*  Standard modal wrapper used everywhere we need a form/panel dialog.       */
/*  Wraps Dialog + DialogContent + DialogHeader + DialogBody + DialogFooter   */
/*  so pages don't have to reassemble the same shell for every editor.        */
/*                                                                            */
/*  Size scale (pixel widths — wider than the old Tailwind sm/md/lg/2xl/3xl   */
/*  so a 6-field editor breathes without horizontal cramp):                   */
/*    sm   ->  480px   (1-3 tight fields — confirm-shaped)                    */
/*    md   ->  720px   (default — 4-6 fields, 2 columns comfortable)          */
/*    lg   ->  960px   (7-8 fields, wide selects, side-by-side inputs)        */
/*    xl   -> 1140px   (dense settings / long descriptions)                   */
/*    full -> 100vw with a 32px inset (wizard modals that must live in a      */
/*           dialog rather than a full page)                                  */
/*                                                                            */
/*  Two patterns are supported:                                               */
/*    1. Pass `footer` — you own the button row.                              */
/*    2. Pass `onSubmit` (and optionally `submitLabel`, `submitDisabled`,     */
/*       `loading`) — AppModal renders a Cancel + Submit footer for you, and  */
/*       wraps the body in a real <form> so Enter submits.                    */
/* -------------------------------------------------------------------------- */

export type AppModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Explicit pixel widths, applied via `className` on `DialogContent`.
 * tailwind-merge (via `cn`) drops the underlying `maxWidthClasses[maxWidth]`
 * so the arbitrary-value class here wins.
 */
const SIZE_CLASS: Record<AppModalSize, string> = {
  sm: 'max-w-[480px]',
  md: 'max-w-[720px]',
  lg: 'max-w-[960px]',
  xl: 'max-w-[1140px]',
  full: 'max-w-[calc(100vw-4rem)]',
};

export interface AppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Full verb + noun sentence, e.g. "Create customer". */
  title: ReactNode;
  /** Optional single-line hint under the title. */
  description?: ReactNode;
  size?: AppModalSize;
  /** Body content. Any form fields go here. */
  children: ReactNode;
  /** Extra classes for the DialogBody (default is `space-y-4`). */
  bodyClassName?: string;

  /* --- Footer: either mode (a) or mode (b) ------------------------------- */

  /** Mode (a): consumer-owned footer. Overrides the auto-generated one. */
  footer?: ReactNode;

  /** Mode (b): auto footer. If supplied, renders Cancel + Submit and wraps
   *  children in a real <form>. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  submitLabel?: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
  submitVariant?: 'default' | 'destructive';
  loading?: boolean;

  /** Hide the automatic Cancel button (rarely wanted). */
  hideCancel?: boolean;
}

export function AppModal({
  open,
  onOpenChange,
  title,
  description,
  size = 'lg',
  children,
  bodyClassName,
  footer,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  submitDisabled,
  submitVariant = 'default',
  loading = false,
  hideCancel = false,
}: AppModalProps) {
  const autoFooter =
    onSubmit && !footer ? (
      <>
        {!hideCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          type="submit"
          variant={submitVariant}
          disabled={submitDisabled}
          loading={loading}
        >
          {submitLabel}
        </Button>
      </>
    ) : null;

  const body = (
    <>
      <DialogBody className={cn('space-y-3', bodyClassName)}>
        {children}
      </DialogBody>
      {(footer || autoFooter) && (
        <DialogFooter className="py-3">{footer ?? autoFooter}</DialogFooter>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className={SIZE_CLASS[size]}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        {onSubmit ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit(event);
            }}
          >
            {body}
          </form>
        ) : (
          body
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AppModal;
